import React, { useState, useContext, useEffect, useMemo } from "react";
import Equipments from "./Equipments";
import DefaultFee from "./DefaultFee";
import { Stack } from "@mui/material";
import CurrencyInput from "./input-elements/CurrencyInput";
import PercentageInput from "./input-elements/PercentageInput";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import { EquipmentContext } from "../useContext/context";
import { Form } from "react-bootstrap";
import * as Yup from "yup";
import pricingValidation from "../pricing-validation";
import ToggleSwitchContainer from "./input-elements/ToggleSwitchContainer";
import { EMS_OTHER_DEFAULT_FEE } from "../../../../constants";
import ACHPricing from "./ACHPricing";
const ic_plus_validation = pricingValidation.pricingValidation.ic_plus;

const ICPlusPricing = ({
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError: removeErrorEq,
    isEditable = true,
}) => {
    const [icPlusDefaults, setIcPlusDefaults] = useState({});
    const { setErrors, pricingDefaults } = useContext(EquipmentContext);

    // Update IC+ defaults when pricing defaults load
    useEffect(() => {
        if (pricingDefaults && pricingDefaults.ic_plus) {
            setIcPlusDefaults(pricingDefaults.ic_plus);
        }
    }, [pricingDefaults]);

    // Set default values when component mounts or IC+ defaults change
    useEffect(() => {
        const defaultValues = {
            ic_plus_rate: icPlusDefaults.ic_plus_rate?.default || ic_plus_validation.ic_plus_rate.default,
            ic_plus_transaction_fee: icPlusDefaults.ic_plus_transaction_fee?.default || ic_plus_validation.ic_plus_transaction_fee.default,
            ic_plus_amex_rate: icPlusDefaults.ic_plus_amex_rate?.default || ic_plus_validation.ic_plus_amex_rate.default,
            ic_plus_amex_transaction_fee: icPlusDefaults.ic_plus_amex_transaction_fee?.default || ic_plus_validation.ic_plus_amex_transaction_fee.default,
            ic_plus_pin_debit_rate: icPlusDefaults.ic_plus_pin_debit_rate?.default || ic_plus_validation.ic_plus_pin_debit_rate.default,
            ic_plus_pin_debit_per_transaction: icPlusDefaults.ic_plus_pin_debit_per_transaction?.default || ic_plus_validation.ic_plus_pin_debit_per_transaction.default,
            ic_plus_pin_debit_network_fee: "no",
            add_ach: "no",
            ebt: "no",
            fnf_number: "",
            ebt_transactions: "",
        };

        // Only set values that aren't already set in form_data
        Object.entries(defaultValues).forEach(([key, value]) => {
            if (form_data[key] === undefined || form_data[key] === "") {
                setFormDataByKeyValue(key, value);
            }
        });
    }, [icPlusDefaults]); // Re-run when IC+ defaults change

    // Create validation schema based on current IC+ defaults
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            ic_plus_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("IC+ Rate is required")
                .min(
                    icPlusDefaults.ic_plus_rate?.minimum || ic_plus_validation.ic_plus_rate.minimum,
                    `Please enter a rate of ${(icPlusDefaults.ic_plus_rate?.minimum || ic_plus_validation.ic_plus_rate.minimum).toFixed(2)}% or higher.`,
                ),

            ic_plus_transaction_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Transaction Fee is required")
                .min(
                    icPlusDefaults.ic_plus_transaction_fee?.minimum || ic_plus_validation.ic_plus_transaction_fee.minimum,
                    `Please enter a fee of $${(icPlusDefaults.ic_plus_transaction_fee?.minimum || ic_plus_validation.ic_plus_transaction_fee.minimum).toFixed(2)} or higher.`,
                ),

            ic_plus_amex_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("AMEX IC+ Rate is required")
                .min(
                    icPlusDefaults.ic_plus_amex_rate?.minimum || ic_plus_validation.ic_plus_amex_rate.minimum,
                    `Please enter a rate of ${(icPlusDefaults.ic_plus_amex_rate?.minimum || ic_plus_validation.ic_plus_amex_rate.minimum).toFixed(2)}% or higher.`,
                ),

            ic_plus_amex_transaction_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("AMEX Transaction Fee is required")
                .min(
                    icPlusDefaults.ic_plus_amex_transaction_fee?.minimum || ic_plus_validation.ic_plus_amex_transaction_fee.minimum,
                    `Please enter a fee of $${(icPlusDefaults.ic_plus_amex_transaction_fee?.minimum || ic_plus_validation.ic_plus_amex_transaction_fee.minimum).toFixed(2)} or higher.`,
                ),

            ic_plus_pin_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Rate is required")
                .min(
                    icPlusDefaults.ic_plus_pin_debit_rate?.minimum || ic_plus_validation.ic_plus_pin_debit_rate.minimum,
                    `Please enter a rate of ${(icPlusDefaults.ic_plus_pin_debit_rate?.minimum || ic_plus_validation.ic_plus_pin_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),

            ic_plus_pin_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Per Transaction fee is required")
                .min(
                    icPlusDefaults.ic_plus_pin_debit_per_transaction?.minimum || ic_plus_validation.ic_plus_pin_debit_per_transaction.minimum,
                    `Please enter a fee of $${(icPlusDefaults.ic_plus_pin_debit_per_transaction?.minimum || ic_plus_validation.ic_plus_pin_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            ic_plus_pin_debit_network_fee: Yup.string().required(
                "Network Fee selection is required",
            ),

            fnf_number: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("ebt", {
                    is: "yes",
                    then: Yup.number().required("FNF Number is Required"),
                    otherwise: Yup.number(),
                }),
        });
    }, [icPlusDefaults]); // Only recreate when icPlusDefaults change

    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        setFormDataByKeyValue(e.target.name, e.target.value);
        validationSchema
            .validateAt(e.target.name, { [e.target.name]: e.target.value })
            .then((e) => {
                // nothing here
            })
            .catch((err) => {
                let validation_errors = errors;
                validation_errors = {
                    ...validation_errors,
                    [e.target.name]: err.errors,
                };
                setErrors(validation_errors);
            });
    };

    return (
        <div className="dual-pricing-section">
            <Stack
                direction={{ xs: "column", sm: "row" }}
                className="container-col-2 mb-4r"
            >
                <div className="top-label-box">
                    <div className="top-label">
                        <img src="/images/visa.png" />
                        <img src="/images/mastercard.png" />
                        <img src="/images/discover.png" />
                    </div>
                    <div className="top-label-box-content">
                        <div className="container-col-2">
                            <div className="w-100">
                                <PercentageInput
                                    label="IC+ Rate"
                                    placeholder={icPlusDefaults.ic_plus_rate?.default || "0.00"}
                                    name={"ic_plus_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.ic_plus_rate}
                                    errors={errors}
                                    softLimit={icPlusDefaults?.ic_plus_rate?.soft_limit}
                                />
                                {errors?.ic_plus_rate &&
                                    Array.isArray(errors.ic_plus_rate) &&
                                    errors.ic_plus_rate.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={icPlusDefaults.ic_plus_transaction_fee?.default || "0.10"}
                                    name={"ic_plus_transaction_fee"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.ic_plus_transaction_fee}
                                    errors={errors}
                                    softLimit={icPlusDefaults?.ic_plus_transaction_fee?.soft_limit}
                                />
                                {errors?.ic_plus_transaction_fee &&
                                    Array.isArray(
                                        errors.ic_plus_transaction_fee,
                                    ) &&
                                    errors.ic_plus_transaction_fee.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="top-label-box">
                    <div className="top-label">
                        <img src="/images/amex.png" />
                    </div>
                    <div className="top-label-box-content">
                        <div className="container-col-2">
                            <div className="w-100">
                                <PercentageInput
                                    label="IC+ Rate"
                                    placeholder={icPlusDefaults.ic_plus_amex_rate?.default || "0.00"}
                                    name={"ic_plus_amex_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.ic_plus_amex_rate}
                                    errors={errors}
                                    softLimit={icPlusDefaults?.ic_plus_amex_rate?.soft_limit}
                                />
                                {errors?.ic_plus_amex_rate &&
                                    Array.isArray(errors.ic_plus_amex_rate) &&
                                    errors.ic_plus_amex_rate.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={icPlusDefaults.ic_plus_amex_transaction_fee?.default || "0.10"}
                                    name={"ic_plus_amex_transaction_fee"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={
                                        form_data.ic_plus_amex_transaction_fee
                                    }
                                    errors={errors}
                                    softLimit={icPlusDefaults?.ic_plus_amex_transaction_fee?.soft_limit}
                                />
                                {errors?.ic_plus_amex_transaction_fee &&
                                    Array.isArray(
                                        errors.ic_plus_amex_transaction_fee,
                                    ) &&
                                    errors.ic_plus_amex_transaction_fee.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </Stack>

            <div className="equipment-box mt-2r">
                <p className="box-label">PIN-based Debit</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-60">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue(
                                    "ic_plus_pin_debit_network_fee",
                                    value,
                                )
                            }
                            value={form_data.ic_plus_pin_debit_network_fee}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="ic_plus_pin_debit_network_fee"
                            label="Network Fee"
                        />
                        {errors?.ic_plus_pin_debit_network_fee &&
                            Array.isArray(
                                errors.ic_plus_pin_debit_network_fee,
                            ) &&
                            errors.ic_plus_pin_debit_network_fee.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                    <div className="w-100">
                        <PercentageInput
                            label="Debit Rate"
                            type="number"
                            placeholder={icPlusDefaults.ic_plus_pin_debit_rate?.default || "0.5"}
                            name={"ic_plus_pin_debit_rate"}
                            onChange={handleOnChange}
                            value={form_data.ic_plus_pin_debit_rate}
                            errors={errors}
                            softLimit={icPlusDefaults?.ic_plus_pin_debit_rate?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.ic_plus_pin_debit_network_fee}
                        />
                        {errors?.ic_plus_pin_debit_rate &&
                            Array.isArray(errors.ic_plus_pin_debit_rate) &&
                            errors.ic_plus_pin_debit_rate.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    <div className="w-100">
                        <CurrencyInput
                            label="Per Txn"
                            type="number"
                            placeholder={icPlusDefaults.ic_plus_pin_debit_per_transaction?.default || "0.15"}
                            name={"ic_plus_pin_debit_per_transaction"}
                            onChange={handleOnChange}
                            value={form_data.ic_plus_pin_debit_per_transaction}
                            errors={errors}
                            softLimit={icPlusDefaults?.ic_plus_pin_debit_per_transaction?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.ic_plus_pin_debit_network_fee}
                        />
                        {errors?.ic_plus_pin_debit_per_transaction &&
                            Array.isArray(
                                errors.ic_plus_pin_debit_per_transaction,
                            ) &&
                            errors.ic_plus_pin_debit_per_transaction.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                </Stack>
            </div>

            <div className="equipment-box mt-2r">
                <p className="box-label">ACH</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-100 d-flex">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("add_ach", value)
                            }
                            value={form_data.add_ach}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="add_ach"
                            label="Add ACH"
                        />
                    </div>
                </Stack>
            </div>

            {form_data.add_ach === "yes" && (
                <ACHPricing
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeErrorEq}
                    isAddOn={true}
                />
            )}

            <div className="equipment-box mt-2r ebt-transaction">
                <p className="box-label">EBT Transactions</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="ebt-switch">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("ebt", value)
                            }
                            value={form_data.ebt}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="ebt"
                            label="EBT"
                        />
                        {errors?.ebt &&
                            Array.isArray(errors.ebt) &&
                            errors.ebt.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    {form_data.ebt == "yes" && (
                        <div className="d-flex w-100">
                            <div className="floating-form w-100">
                                <Form.Group size="lg" controlId="fnf_number">
                                    <Form.Control
                                        placeholder="FNS Number"
                                        type="text"
                                        name="fnf_number"
                                        value={form_data.fnf_number}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleOnChange(e);
                                            }
                                        }}
                                        className="input-control"
                                    />
                                    <Form.Label className="floating-label">
                                        FNF Number
                                    </Form.Label>
                                </Form.Group>
                                {errors?.fnf_number &&
                                    Array.isArray(errors.fnf_number) &&
                                    errors.fnf_number.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="floating-form w-100">
                                <CurrencyInput
                                    label="EBT Transactions"
                                    endAdornment="/txn"
                                    placeholder={
                                        EMS_OTHER_DEFAULT_FEE.ebt_transactions
                                    }
                                    value={form_data.ebt_transactions}
                                    name={"ebt_transactions"}
                                    type="number"
                                    onChange={handleOnChange}
                                    errors={errors}
                                    softLimit={icPlusDefaults?.ebt_transactions?.soft_limit}
                                />
                                {errors?.ebt_transactions &&
                                    Array.isArray(errors.ebt_transactions) &&
                                    errors.ebt_transactions.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                </Stack>
            </div>

            <DefaultFee
                form_data={form_data}
                setFormDataByKeyValue={setFormDataByKeyValue}
                errors={errors}
            />

            {isEditable && (
                <Equipments
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    form_data={form_data}
                    errors={errors}
                    allEquipments={allEquipments}
                    removeError={removeErrorEq}
                />
            )}
        </div>
    );
};

export default ICPlusPricing;
